import React, { useState, useEffect, useContext, useRef } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import InputAdornment from "@material-ui/core/InputAdornment";
import IconButton from "@material-ui/core/IconButton";
import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import ChatiaDialogActions from "../ChatiaDialogActions";
import QueueSelect from "../QueueSelect";
import { AuthContext } from "../../context/Auth/AuthContext";
import useWhatsApps from "../../hooks/useWhatsApps";

import { Can } from "../Can";
import {
  Grid,
  Paper,
  Tab,
  Tabs,
  Typography,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import {
  X as LucideX,
  CheckSquare,
  Square,
  Eye,
  EyeOff,
} from "lucide-react";
import TabPanel from "../TabPanel";
import AvatarUploader from "../AvatarUpload";
import CustomToggleSwitch from "../CustomToggleSwitch";
import { SelectArrowIcon } from "../ChatiaIcons";
import useFilterDropdownStyles from "../SharedStyles/useFilterDropdownStyles";

const WORK_TIME_OPTIONS = (() => {
  const opts = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 15) {
      opts.push(
        `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`
      );
    }
  }
  return opts;
})();

const formatTimeInput = (raw) => {
  const digits = (raw || "").replace(/[^0-9]/g, "");
  if (digits.length >= 4 && !raw.includes(":")) {
    const hh = Math.min(parseInt(digits.slice(0, 2), 10), 23);
    const mm = Math.min(parseInt(digits.slice(2, 4), 10), 59);
    return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
  }
  return raw;
};

const PERMISSION_FIELDS = [
  {
    name: "canViewAllContacts",
    labelKey: "userModal.form.canViewAllContacts",
    isBoolean: true,
  },
  {
    name: "allTicket",
    labelKey: "userModal.form.allTicket",
    onValue: "enable",
    offValue: "disable",
  },
  {
    name: "allowGroup",
    labelKey: "userModal.form.allowGroup",
    isBoolean: true,
  },
  {
    name: "allHistoric",
    labelKey: "userModal.form.allHistoric",
    onValue: "enabled",
    offValue: "disabled",
  },
  {
    name: "allUserChat",
    labelKey: "userModal.form.allUserChat",
    onValue: "enabled",
    offValue: "disabled",
  },
  {
    name: "userClosePendingTicket",
    labelKey: "userModal.form.userClosePendingTicket",
    onValue: "enabled",
    offValue: "disabled",
  },
  {
    name: "allowConnections",
    labelKey: "userModal.form.allowConnections",
    onValue: "enabled",
    offValue: "disabled",
  },
  {
    name: "showDashboard",
    labelKey: "userModal.form.showDashboard",
    onValue: "enabled",
    offValue: "disabled",
  },
  {
    name: "allowRealTime",
    labelKey: "userModal.form.allowRealTime",
    onValue: "enabled",
    offValue: "disabled",
  },
];

const useStyles = makeStyles((theme) => {
  const ds = theme.chatiaDS || {};
  const dsColors = ds.colors || {};
  const isDark = theme.palette.type === "dark";
  return {
    root: {
      display: "flex",
      flexWrap: "wrap",
    },
    dialogPaper: {
      ...(ds.dialogPaper || {}),
    },
    header: {
      ...(ds.header || {}),
      flexShrink: 0,
    },
    headerTitle: {
      ...(ds.headerTitle || {}),
    },
    closeButton: {
      backgroundColor:
        ds.closeButton?.backgroundColor || "rgba(255,255,255,0.08)",
      color: ds.closeButton?.color || "#ffffff",
      transition: ds.closeButton?.transition || "all 0.2s ease",
      "&:hover": {
        backgroundColor: "rgba(255, 255, 255, 0.18)",
        color: "#ffffff",
      },
    },
    tabsPaper: {
      flexShrink: 0,
      background: isDark ? "rgba(255,255,255,0.02)" : "rgba(15,23,42,0.03)",
      borderBottom: `1px solid ${
        dsColors.border || (isDark ? "#202632" : "#e8e8e8")
      }`,
      borderRadius: 0,
      boxShadow: "none",
    },
    tabs: {
      minHeight: 42,
      "& .MuiTab-root": {
        minHeight: 42,
        textTransform: "none",
        fontSize: 13,
        fontWeight: 500,
        minWidth: "auto",
        padding: "6px 18px",
        color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
        "&.Mui-selected": {
          color: theme.palette.primary.main,
          fontWeight: 600,
        },
      },
      "& .MuiTabs-indicator": {
        height: 2,
        borderRadius: 2,
      },
    },
    dialogContent: {
      padding: "16px 20px !important",
      ...theme.scrollbarStyles,
    },
    multFieldLine: {
      display: "flex",
      "& > *:not(:last-child)": {
        marginRight: theme.spacing(1),
      },
    },
    btnWrapper: {
      position: "relative",
    },
    buttonProgress: {
      color: green[500],
      position: "absolute",
      top: "50%",
      left: "50%",
      marginTop: -12,
      marginLeft: -12,
    },
    formControl: {
      margin: theme.spacing(1),
      minWidth: 120,
    },
    textField: {
      marginRight: theme.spacing(1),
      flex: 1,
    },
    container: {
      display: "flex",
      flexWrap: "wrap",
    },
    avatar: {
      width: theme.spacing(12),
      height: theme.spacing(12),
      margin: theme.spacing(2),
      cursor: "pointer",
      borderRadius: "50%",
      border: "2px solid #ccc",
    },
    updateDiv: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
    },
    updateInput: {
      display: "none",
    },
    updateLabel: {
      padding: theme.spacing(1),
      margin: theme.spacing(1),
      textTransform: "uppercase",
      textAlign: "center",
      cursor: "pointer",
      border: "2px solid #ccc",
      borderRadius: "5px",
      minWidth: 160,
      fontWeight: "bold",
      color: "#555",
    },
    errorUpdate: {
      border: "2px solid red",
    },
    errorText: {
      color: "red",
      fontSize: "0.8rem",
      fontWeight: "bold",
    },
    sectionTitle: {
      fontSize: 13,
      fontWeight: 600,
      color: dsColors.textPrimary || (isDark ? "#e9edef" : "#111b21"),
      marginTop: 4,
      marginBottom: 8,
      textTransform: "uppercase",
      letterSpacing: 0.5,
    },
    sectionDivider: {
      height: 1,
      width: "100%",
      background: dsColors.border || (isDark ? "#202632" : "#e8e8e8"),
      margin: "14px 0 6px 0",
    },
    toggleCard: {
      display: "flex",
      flexDirection: "column",
      padding: "10px 16px",
      borderRadius: 10,
      border: isDark
        ? "1px solid rgba(255,255,255,0.10)"
        : "1px solid rgba(0,0,0,0.10)",
      background: isDark ? "rgba(255,255,255,0.02)" : "transparent",
      transition: "all 0.2s ease",
      "&:hover": {
        backgroundColor: isDark ? "rgba(255,255,255,0.05)" : "#f5f5f5",
        borderColor: isDark ? "rgba(255,255,255,0.20)" : "#bdbdbd",
      },
    },
    toggleCardRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      width: "100%",
      gap: 12,
    },
    toggleLabel: {
      fontSize: "0.9rem",
      color: isDark ? "#e9edef" : "#424242",
      fontWeight: 500,
      flex: 1,
      paddingRight: 12,
    },
    sectionRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      marginTop: 4,
      marginBottom: 8,
      flexWrap: "wrap",
    },
    sectionLabel: {
      fontSize: 12,
      fontWeight: 600,
      textTransform: "uppercase",
      letterSpacing: 0.6,
      color: isDark ? "#8696a0" : "#667781",
    },
    bulkActions: {
      display: "inline-flex",
      gap: 6,
    },
    bulkBtn: {
      borderRadius: 10,
      textTransform: "none",
      fontWeight: 500,
      fontSize: 12,
      padding: "6px 12px",
      gap: 6,
      border: "none",
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      color: isDark ? "#e9edef" : "#0f172a",
      background: isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)",
      transition: "background 0.18s ease",
      "&:hover": {
        background: isDark ? "rgba(255,255,255,0.10)" : "rgba(15,23,42,0.10)",
      },
    },
    removeAvatarBtn: {
      marginTop: 6,
      borderRadius: 10,
      textTransform: "none",
      fontSize: 12.5,
    },
  };
});

const UserSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, i18n.t("validation.tooShort"))
    .max(50, i18n.t("validation.tooLong"))
    .required(i18n.t("validation.required")),
  password: Yup.string()
    .min(5, i18n.t("validation.tooShort"))
    .max(50, i18n.t("validation.tooLong")),
  email: Yup.string()
    .email(i18n.t("validation.invalidEmail"))
    .required(i18n.t("validation.required")),
  allHistoric: Yup.string().nullable(),
});

const UserModal = ({ open, onClose, onSaved, userId }) => {
  const classes = useStyles();
  const dropdownClasses = useFilterDropdownStyles();

  const selectMenuProps = {
    classes: { paper: dropdownClasses.paper },
    anchorOrigin: { vertical: "bottom", horizontal: "left" },
    transformOrigin: { vertical: "top", horizontal: "left" },
    getContentAnchorEl: null,
    PaperProps: { style: { marginTop: 5 } },
  };

  const initialState = {
    name: "",
    email: "",
    password: "",
    profile: "user",
    startWork: "00:00",
    endWork: "23:59",
    farewellMessage: "",
    allTicket: "disable",
    allowGroup: false,
    defaultTheme: "light",
    defaultMenu: "open",
    allHistoric: "disabled",
    allUserChat: "disabled",
    userClosePendingTicket: "enabled",
    showDashboard: "disabled",
    allowRealTime: "disabled",
    allowConnections: "disabled",
    canViewAllContacts: false,
  };

  const { user: loggedInUser } = useContext(AuthContext);

  const [user, setUser] = useState(initialState);
  const [selectedQueueIds, setSelectedQueueIds] = useState([]);
  const [whatsappId, setWhatsappId] = useState(false);
  const { whatsApps } = useWhatsApps();
  const [tab, setTab] = useState("general");
  const [avatar, setAvatar] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const startWorkRef = useRef();
  const endWorkRef = useRef();

  useEffect(() => {
    let isMounted = true;

    const fetchUser = async () => {
      if (!userId) return;
      try {
        const { data } = await api.get(`/users/${userId}`);

        if (!isMounted) return;

        const userData = {
          ...data,
          canViewAllContacts: !!data.canViewAllContacts,
        };

        setUser((prevState) => {
          return { ...prevState, ...userData };
        });

        const userQueueIds = data.queues?.map((queue) => queue.id);
        setSelectedQueueIds(userQueueIds);
        setWhatsappId(data.whatsappId ? data.whatsappId : "");
      } catch (err) {
        if (isMounted) {
          toastError(err);
        }
      }
    };

    fetchUser();

    return () => {
      isMounted = false;
    };
  }, [userId, open]);

  const handleClose = () => {
    onClose();
    setUser(initialState);
    setTab("general");
  };

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
  };

  const handleSaveUser = async (values) => {
    const uploadAvatar = async (file) => {
      try {
        const formData = new FormData();
        formData.append("userId", file.id);
        formData.append("typeArch", "user");
        formData.append("profileImage", avatar);

        const { data } = await api.post(
          `/users/${file.id}/media-upload`,
          formData
        );
        localStorage.setItem("profileImage", data.user.profileImage);
      } catch (err) {
        toastError(err);
      }
    };

    const userData = {
      ...values,
      whatsappId,
      queueIds: selectedQueueIds,
    };

    try {
      let savedUser;
      if (userId) {
        const { data } = await api.put(`/users/${userId}`, userData);
        savedUser = data;

        if (
          avatar &&
          (!user?.profileImage || user?.profileImage !== avatar.name)
        ) {
          await uploadAvatar(data);
        }
      } else {
        const { data } = await api.post("/users", userData);
        savedUser = data;

        if (avatar) {
          await uploadAvatar(data);
        }
      }

      if (onSaved && savedUser) {
        onSaved(savedUser);
      }

      if (userId === loggedInUser.id) {
        handleClose();
        toast.success(i18n.t("userModal.success"));
        window.location.reload();
      } else {
        handleClose();
        toast.success(i18n.t("userModal.success"));
      }
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        scroll="paper"
        PaperProps={{ className: classes.dialogPaper }}
      >
        <div className={classes.header}>
          <Typography className={classes.headerTitle}>
            {userId
              ? `${i18n.t("userModal.title.edit")}`
              : `${i18n.t("userModal.title.add")}`}
          </Typography>
          <IconButton
            size="small"
            onClick={handleClose}
            className={classes.closeButton}
          >
            <LucideX size={18} strokeWidth={2.2} />
          </IconButton>
        </div>
        <Formik
          initialValues={user}
          enableReinitialize={true}
          validationSchema={UserSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveUser(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ touched, errors, isSubmitting, setFieldValue }) => (
            <Form>
              <Paper className={classes.tabsPaper} elevation={0} square>
                <Tabs
                  value={tab}
                  indicatorColor="primary"
                  textColor="primary"
                  scrollButtons="on"
                  variant="scrollable"
                  onChange={handleTabChange}
                  className={classes.tabs}
                >
                  <Tab
                    label={i18n.t("userModal.tabs.general")}
                    value={"general"}
                  />
                  <Tab
                    label={i18n.t("userModal.tabs.permissions")}
                    value={"permissions"}
                  />
                </Tabs>
              </Paper>
              <DialogContent dividers className={classes.dialogContent}>
                <TabPanel
                  className={classes.container}
                  value={tab}
                  name={"general"}
                >
                  <Grid
                    container
                    spacing={1}
                    alignContent="center"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <FormControl className={classes.updateDiv}>
                      <AvatarUploader
                        setAvatar={setAvatar}
                        avatar={user.profileImage}
                        companyId={user.companyId}
                      />
                      {user.profileImage && (
                        <Button
                          variant="outlined"
                          color="secondary"
                          size="small"
                          className={classes.removeAvatarBtn}
                          onClick={() => {
                            user.profileImage = null;
                            setFieldValue("profileImage", null);
                            setAvatar(null);
                          }}
                        >
                          {i18n.t("userModal.title.removeImage")}
                        </Button>
                      )}
                    </FormControl>
                  </Grid>

                  <Typography className={classes.sectionTitle}>
                    {i18n.t("userModal.tabs.general")}
                  </Typography>

                  <Grid container spacing={1}>
                    <Grid item xs={12} md={6} xl={6}>
                      <Field
                        as={TextField}
                        label={i18n.t("userModal.form.name")}
                        autoFocus
                        name="name"
                        error={touched.name && Boolean(errors.name)}
                        helperText={touched.name && errors.name}
                        variant="outlined"
                        margin="dense"
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={6} xl={6}>
                      <Field
                        as={TextField}
                        label={i18n.t("userModal.form.password")}
                        type={showPassword ? "text" : "password"}
                        name="password"
                        error={touched.password && Boolean(errors.password)}
                        helperText={touched.password && errors.password}
                        variant="outlined"
                        margin="dense"
                        fullWidth
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="toggle password visibility"
                                onClick={() => setShowPassword(!showPassword)}
                                onMouseDown={(e) => e.preventDefault()}
                                edge="end"
                                size="small"
                              >
                                {showPassword ? (
                                  <EyeOff size={18} strokeWidth={2} />
                                ) : (
                                  <Eye size={18} strokeWidth={2} />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  </Grid>
                  <Grid container spacing={1}>
                    <Grid item xs={12} md={8} xl={8}>
                      <Field
                        as={TextField}
                        label={i18n.t("userModal.form.email")}
                        name="email"
                        error={touched.email && Boolean(errors.email)}
                        helperText={touched.email && errors.email}
                        variant="outlined"
                        margin="dense"
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={4} xl={4}>
                      <FormControl
                        variant="outlined"
                        margin="dense"
                        size="small"
                        fullWidth
                      >
                        <Can
                          role={loggedInUser.profile}
                          perform="user-modal:editProfile"
                          yes={() => (
                            <>
                              <InputLabel id="profile-selection-input-label">
                                {i18n.t("userModal.form.profile")}
                              </InputLabel>

                              <Field name="profile">
                                {({ field, form }) => (
                                  <Select
                                    {...field}
                                    label={i18n.t("userModal.form.profile")}
                                    labelId="profile-selection-label"
                                    id="profile-selection"
                                    required
                                    IconComponent={SelectArrowIcon}
                                    MenuProps={selectMenuProps}
                                    onChange={(e) => {
                                      field.onChange(e);
                                      if (e.target.value === "admin") {
                                        PERMISSION_FIELDS.forEach((p) =>
                                          form.setFieldValue(
                                            p.name,
                                            p.isBoolean ? true : p.onValue
                                          )
                                        );
                                      }
                                    }}
                                  >
                                    <MenuItem value="admin">
                                      {i18n.t("users.profile.admin")}
                                    </MenuItem>
                                    <MenuItem value="user">
                                      {i18n.t("users.profile.user")}
                                    </MenuItem>
                                  </Select>
                                )}
                              </Field>
                            </>
                          )}
                        />
                      </FormControl>
                    </Grid>
                  </Grid>
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <Can
                        role={loggedInUser.profile}
                        perform="user-modal:editQueues"
                        yes={() => (
                          <QueueSelect
                            selectedQueueIds={selectedQueueIds}
                            onChange={(values) =>
                              setSelectedQueueIds(values)
                            }
                            fullWidth
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <Can
                        role={loggedInUser.profile}
                        perform="user-modal:editProfile"
                        yes={() => (
                          <FormControl
                            variant="outlined"
                            margin="dense"
                            size="small"
                            fullWidth
                          >
                            <InputLabel>
                              {i18n.t("userModal.form.whatsapp")}
                            </InputLabel>
                            <Field
                              as={Select}
                              value={whatsappId}
                              onChange={(e) => setWhatsappId(e.target.value)}
                              label={i18n.t("userModal.form.whatsapp")}
                              IconComponent={SelectArrowIcon}
                              MenuProps={selectMenuProps}
                            >
                              <MenuItem value={""}>&nbsp;</MenuItem>
                              {whatsApps.map((whatsapp) => (
                                <MenuItem
                                  key={whatsapp.id}
                                  value={whatsapp.id}
                                >
                                  {whatsapp.name}
                                </MenuItem>
                              ))}
                            </Field>
                          </FormControl>
                        )}
                      />
                    </Grid>
                  </Grid>
                  <Can
                    role={loggedInUser.profile}
                    perform="user-modal:editProfile"
                    yes={() => (
                      <>
                        <div className={classes.sectionDivider} />
                        <Typography className={classes.sectionTitle}>
                          {i18n.t("userModal.form.startWork")} /{" "}
                          {i18n.t("userModal.form.endWork")}
                        </Typography>
                        <Grid container spacing={1}>
                          <Grid item xs={12} md={6} xl={6}>
                            <Field name="startWork">
                              {({ field, form }) => (
                                <Autocomplete
                                  freeSolo
                                  size="small"
                                  classes={{ paper: dropdownClasses.paper }}
                                  options={WORK_TIME_OPTIONS}
                                  inputValue={field.value || ""}
                                  onInputChange={(e, v, reason) => {
                                    if (reason === "reset") return;
                                    form.setFieldValue(
                                      "startWork",
                                      formatTimeInput(v)
                                    );
                                  }}
                                  onChange={(e, v) => {
                                    if (!v) return;
                                    form.setFieldValue("startWork", v);
                                  }}
                                  disableClearable
                                  getOptionLabel={(o) => o || ""}
                                  ListboxProps={{ style: { maxHeight: 200 } }}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      variant="outlined"
                                      margin="dense"
                                      fullWidth
                                      label={i18n.t("userModal.form.startWork")}
                                      placeholder="00:00"
                                      InputLabelProps={{ shrink: true }}
                                      inputRef={startWorkRef}
                                      error={
                                        touched.startWork &&
                                        Boolean(errors.startWork)
                                      }
                                      helperText={
                                        touched.startWork && errors.startWork
                                      }
                                    />
                                  )}
                                />
                              )}
                            </Field>
                          </Grid>
                          <Grid item xs={12} md={6} xl={6}>
                            <Field name="endWork">
                              {({ field, form }) => (
                                <Autocomplete
                                  freeSolo
                                  size="small"
                                  classes={{ paper: dropdownClasses.paper }}
                                  options={WORK_TIME_OPTIONS}
                                  inputValue={field.value || ""}
                                  onInputChange={(e, v, reason) => {
                                    if (reason === "reset") return;
                                    form.setFieldValue(
                                      "endWork",
                                      formatTimeInput(v)
                                    );
                                  }}
                                  onChange={(e, v) => {
                                    if (!v) return;
                                    form.setFieldValue("endWork", v);
                                  }}
                                  disableClearable
                                  getOptionLabel={(o) => o || ""}
                                  ListboxProps={{ style: { maxHeight: 200 } }}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      variant="outlined"
                                      margin="dense"
                                      fullWidth
                                      label={i18n.t("userModal.form.endWork")}
                                      placeholder="23:59"
                                      InputLabelProps={{ shrink: true }}
                                      inputRef={endWorkRef}
                                      error={
                                        touched.endWork &&
                                        Boolean(errors.endWork)
                                      }
                                      helperText={
                                        touched.endWork && errors.endWork
                                      }
                                    />
                                  )}
                                />
                              )}
                            </Field>
                          </Grid>
                        </Grid>
                      </>
                    )}
                  />

                  <div className={classes.sectionDivider} />
                  <Typography className={classes.sectionTitle}>
                    {i18n.t("userModal.form.farewellMessage")}
                  </Typography>
                  <Field
                    as={TextField}
                    label={i18n.t("userModal.form.farewellMessage")}
                    type="farewellMessage"
                    multiline
                    minRows={4}
                    fullWidth
                    name="farewellMessage"
                    error={
                      touched.farewellMessage &&
                      Boolean(errors.farewellMessage)
                    }
                    helperText={
                      touched.farewellMessage && errors.farewellMessage
                    }
                    variant="outlined"
                    margin="dense"
                  />

                  <div className={classes.sectionDivider} />
                  <Typography className={classes.sectionTitle}>
                    {i18n.t("userModal.form.defaultTheme")}
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={12} md={6} xl={6}>
                      <FormControl
                        variant="outlined"
                        margin="dense"
                        size="small"
                        fullWidth
                      >
                        <InputLabel>
                          {i18n.t("userModal.form.defaultTheme")}
                        </InputLabel>
                        <Field
                          as={Select}
                          label={i18n.t("userModal.form.defaultTheme")}
                          name="defaultTheme"
                          type="defaultTheme"
                          required
                          IconComponent={SelectArrowIcon}
                          MenuProps={selectMenuProps}
                        >
                          <MenuItem value="light">
                            {i18n.t("userModal.form.defaultThemeLight")}
                          </MenuItem>
                          <MenuItem value="dark">
                            {i18n.t("userModal.form.defaultThemeDark")}
                          </MenuItem>
                        </Field>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6} xl={6}>
                      <FormControl
                        variant="outlined"
                        margin="dense"
                        size="small"
                        fullWidth
                      >
                        <InputLabel>
                          {i18n.t("userModal.form.defaultMenu")}
                        </InputLabel>
                        <Field
                          as={Select}
                          label={i18n.t("userModal.form.defaultMenu")}
                          name="defaultMenu"
                          type="defaultMenu"
                          required
                          IconComponent={SelectArrowIcon}
                          MenuProps={selectMenuProps}
                        >
                          <MenuItem value={"open"}>
                            {i18n.t("userModal.form.defaultMenuOpen")}
                          </MenuItem>
                          <MenuItem value={"closed"}>
                            {i18n.t("userModal.form.defaultMenuClosed")}
                          </MenuItem>
                        </Field>
                      </FormControl>
                    </Grid>
                  </Grid>
                </TabPanel>
                <TabPanel
                  className={classes.container}
                  value={tab}
                  name={"permissions"}
                >
                  <Can
                    role={loggedInUser.profile}
                    perform="user-modal:editProfile"
                    yes={() => (
                      <Grid container spacing={1}>
                        <Grid item xs={12}>
                          <div className={classes.sectionRow}>
                            <Typography className={classes.sectionLabel}>
                              {i18n.t("userModal.tabs.permissions")}
                            </Typography>
                            <div className={classes.bulkActions}>
                              <button
                                type="button"
                                className={classes.bulkBtn}
                                onClick={() =>
                                  PERMISSION_FIELDS.forEach((p) =>
                                    setFieldValue(
                                      p.name,
                                      p.isBoolean ? true : p.onValue
                                    )
                                  )
                                }
                              >
                                <CheckSquare size={14} strokeWidth={1.8} />
                                {i18n.t("plans.modal.markAll") ||
                                  "Marcar todas"}
                              </button>
                              <button
                                type="button"
                                className={classes.bulkBtn}
                                onClick={() =>
                                  PERMISSION_FIELDS.forEach((p) =>
                                    setFieldValue(
                                      p.name,
                                      p.isBoolean ? false : p.offValue
                                    )
                                  )
                                }
                              >
                                <Square size={14} strokeWidth={1.8} />
                                {i18n.t("plans.modal.unmarkAll") ||
                                  "Desmarcar todas"}
                              </button>
                            </div>
                          </div>
                        </Grid>
                        {PERMISSION_FIELDS.map((perm) => (
                          <Grid item xs={12} md={6} key={perm.name}>
                            <Field name={perm.name}>
                              {({ field, form }) => {
                                const isOn = perm.isBoolean
                                  ? !!field.value
                                  : field.value === perm.onValue;
                                return (
                                  <div className={classes.toggleCard}>
                                    <div className={classes.toggleCardRow}>
                                      <span className={classes.toggleLabel}>
                                        {i18n.t(perm.labelKey)}
                                      </span>
                                      <CustomToggleSwitch
                                        checked={isOn}
                                        onChange={(e) => {
                                          const val = perm.isBoolean
                                            ? e.target.checked
                                            : e.target.checked
                                            ? perm.onValue
                                            : perm.offValue;
                                          form.setFieldValue(perm.name, val);
                                        }}
                                      />
                                    </div>
                                  </div>
                                );
                              }}
                            </Field>
                          </Grid>
                        ))}
                      </Grid>
                    )}
                  />
                </TabPanel>
              </DialogContent>
              <ChatiaDialogActions
                type="submit"
                disabled={isSubmitting}
                loading={isSubmitting}
                saveText={
                  userId
                    ? `${i18n.t("userModal.buttons.okEdit")}`
                    : `${i18n.t("userModal.buttons.okAdd")}`
                }
              />
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default UserModal;
